import {Component, OnInit} from '@angular/core';
import {LangSrcService} from './services/lang-src/lang-src.service';
import {PlatformService} from './services/platform/platform.service';
import {NgbModal, NgbModalConfig} from '@ng-bootstrap/ng-bootstrap';
import {LangTgtService} from './services/lang-tgt/lang-tgt.service';
import {LearningModalSameLanguageComponent} from './pages/learning/learning-modal-same-language/learning-modal-same-language.component';
import {PageService} from './services/page/page.service';
import {FullscreenService} from './services/fullscreen/fullscreen.service';
import {ConfigService} from './services/config/config.service';
import {AuthService} from './services/auth/auth.service';
import {VersionService} from './services/version/version.service';
import {RedirectService} from './services/redirect/redirect.service';
import {UserService} from './services/user/user.service';
import {WefitSsoService} from './services/sso/wefit-sso.service';
import {TranslateService} from '@ngx-translate/core';
import {TranslationOverrideService} from './services/translation-override/translation-override.service';
import {TranslationTrackerService} from './services/translation-tracker/translation-tracker.service';
import AOS from 'aos';
import {firstValueFrom} from 'rxjs';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';
import {CommonModule} from '@angular/common';
import {SidebarComponent} from './components/sidebar/sidebar.component';
import {PageHeaderComponent} from './components/page-header/page-header.component';
import {AdminToolbarComponent} from './components/admin-toolbar/admin-toolbar.component';

try {
  window.addEventListener('touchmove', () => {
  }, {passive: false});
} catch (e) {
  console.error(e.toString());
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    SidebarComponent,
    PageHeaderComponent,
    RouterModule,
    AdminToolbarComponent
  ],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {

  loading = true;
  isLogged = false;
  isAdmin = false;
  private readonly TOOLBAR_ROLES = ['ROLE_ADMIN', 'ROLE_TRANSLATOR'];

  constructor(
    private platformSrv: PlatformService,
    public langSrcSrv: LangSrcService,
    private langTgtSrv: LangTgtService,
    private fullscreenSrv: FullscreenService,
    private configSrv: ConfigService,
    public pageSrv: PageService,
    private modalConfig: NgbModalConfig,
    private modal: NgbModal,
    private versionSrv: VersionService,
    private authSrv: AuthService,
    private redirectSrv: RedirectService,
    private userSrv: UserService,
    private wefitSsoSrv: WefitSsoService,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private overrideSrv: TranslationOverrideService,
    private tracker: TranslationTrackerService
  ) {
    AOS.init();
    // set modal configuration
    this.modalConfig.backdrop = 'static';
    this.modalConfig.keyboard = false;

    // Lire ssoToken & lg depuis window.location AVANT toute opération async —
    // ActivatedRoute n'est pas fiable si la navigation initiale est annulée ou redirigée
    const urlParams = new URLSearchParams(window.location.search);
    const ssoToken = urlParams.get('ssoToken');
    const lg = urlParams.get('lg');

    // [Reload] deprecated app version
    this.versionSrv.check().then(async () => {
      try {
        // STEP 1: Initialiser page (définit langSrcSrv.currentCode)
        await this.pageSrv.initialize();

        // STEP 2: Initialiser langSrc (doit être après page)
        await this.langSrcSrv.initialize();

        // STEP 3: Paralléliser les initialisations indépendantes
        this.fullscreenSrv.initialize();
        await Promise.all([
          this.platformSrv.initialize(),
          this.configSrv.initialize(),
          this.langTgtSrv.initialize()
        ]);

        // STEP 4: SSO inbound — stocker le token & user (modal & redirect plus bas)
        if (ssoToken) {
          await this.authSrv.storeToken(ssoToken);
          await this.wefitSsoSrv.clearToken();
          await this.authSrv.storeUser();
          this.isLogged = true;
        }

        // STEP 5: lg param — wefit force la langue apprise (platform).
        // Si lg === langSrc actuelle, ouvrir la modal "même langue" pour que
        // l'utilisateur change sa langue d'interface avant de continuer.
        if (lg) {
          const isAuth = this.isLogged || await this.authSrv.isReallyLogged();
          const sameLanguage = isAuth && lg === this.langSrcSrv.current?.shortCode;
          let langSrcChanged = false;
          if (sameLanguage) {
            langSrcChanged = await this.openSameLanguageModal(lg);
            // Quand langSrc change, la liste /langs_tgt change aussi (lg devient disponible
            // comme cible alors qu'il était filtré quand c'était la langSrc), donc on refetch.
            if (langSrcChanged) {
              this.langTgtSrv.initialized = false;
              await this.langTgtSrv.initialize();
            }
          }
          // Si l'utilisateur a ignoré la modal (langSrc inchangée alors qu'on était en
          // conflit), on ne force PAS la plateforme — sinon on retomberait sur lg === langSrc.
          // On garde sa plateforme actuelle.
          if (!sameLanguage || langSrcChanged) {
            await this.platformSrv.forceSetPlatformFromCode(lg);
            const newLangTgt = this.langTgtSrv.langs?.find((l: any) => l.code === lg);
            if (newLangTgt) {
              await this.langTgtSrv.forceSetCurrent(newLangTgt);
            }
          }
        }

        // STEP 6: SSO redirect vers le dashboard
        if (ssoToken) {
          const lang = this.langSrcSrv.current?.shortCode || 'fr';
          await this.router.navigate(['/', lang, 'learning', 'dashboard'], { replaceUrl: true });
          return;
        }

        // STEP 7: Vérifier l'authentification classique
        this.isLogged = await this.authSrv.isReallyLogged();

        // STEP 8: Détecter si l'utilisateur a accès au toolbar (ROLE_ADMIN ou ROLE_TRANSLATOR)
        this.isAdmin = await this.computeIsAdmin();

        // STEP 9: Charger les overrides de traductions et les injecter dans ngx-translate
        await this.applyTranslationOverrides();

        // STEP 10: Démarrer le tracker de clés pour le toolbar admin
        if (this.isAdmin) {
          this.tracker.start();
        }

      } catch (error) {
        console.error('GS: Error during initialization:', error);
      } finally {
        this.loading = false;
      }
    });


    // init global events
    this.initGlobalEvents();

    this.authSrv.signInEvent.subscribe(async () => {
      this.isLogged = await this.authSrv.isReallyLogged();
      this.isAdmin = await this.computeIsAdmin();
      if (this.isAdmin) {
        this.tracker.start();
      }
    });
  }

  ngOnInit() {}

  initGlobalEvents() {
    // click on play button
    this.redirectSrv.playSubject.subscribe(async (params) => {
      await this.redirectSrv.play(params);
    });
  }

  private async computeIsAdmin(): Promise<boolean> {
    if (!this.isLogged) {
      return false;
    }
    try {
      const user: any = await this.authSrv.getUser();
      const roles: string[] = Array.isArray(user?.roles) ? user.roles : [];
      return roles.some(r => this.TOOLBAR_ROLES.includes(r));
    } catch {
      return false;
    }
  }

  private async openSameLanguageModal(lg: string): Promise<boolean> {
    // L'API /langs_tgt n'inclut pas la langue source de l'utilisateur (on ne peut pas
    // apprendre sa propre langue), donc lg === langSrc → langTgt absent. On reconstitue
    // un objet minimal depuis la langue source pour l'affichage de la modal.
    let langTgt: any = this.langTgtSrv.langs?.find((l: any) => l.code === lg);
    if (!langTgt) {
      const src = this.langSrcSrv.current;
      if (src?.shortCode === lg) {
        langTgt = { code: src.shortCode, name: src.name, flag: src.flag };
      }
    }
    if (!langTgt) {
      return false;
    }
    // Attendre que les traductions de la langue courante soient chargées —
    // sinon le pipe `translate` affiche les clés (qui sont en français).
    const code = this.langSrcSrv.current?.shortCode;
    if (code) {
      try {
        await firstValueFrom(this.translate.use(code));
      } catch {
        // tant pis, on ouvre quand même
      }
    }
    try {
      const modalRef = this.modal.open(LearningModalSameLanguageComponent, {
        backdrop: 'static',
        keyboard: false
      });
      modalRef.componentInstance.langTgt = langTgt;
      const result = await modalRef.result;
      return result === true;
    } catch {
      // dismissed — on continue quand même
      return false;
    }
  }

  private async applyTranslationOverrides(): Promise<void> {
    const shortCode = this.langSrcSrv.current?.shortCode;
    if (!shortCode) {
      return;
    }
    try {
      const overrides = await this.overrideSrv.loadForLangSrc(shortCode);
      if (overrides && Object.keys(overrides).length > 0) {
        this.translate.setTranslation(shortCode, overrides, true);
      }
    } catch (e) {
      console.error('GS: Failed to load translation overrides', e);
    }
  }
}
